import React from 'react';
// import SarasDnDProvider from '../../../library/hooks/bdnd/SarasDnDProvider';
// import { SarasDnDTypes } from '../../../library/hooks/bdnd/SarasDnDTypes';
import SarasDnDProvider from '../../../library/hooks/dndkit/SarasDnDProvider';
import { SarasDnDTypes } from '../../../library/hooks/dndkit/SarasDnDTypes';
import SarasMsg from '../../../library/notifications/SarasMsg';
import CalGridBoard from './components/CalGridBoard';
import useCalendarControl from './hooks/useCalendarControl';
import useSarasCalendarDnDManager from './hooks/useSarasCalendarDnDManager';
import {
    applyPlanDateMoveLocalChanges,
    applyPlanWeekLocalChanges,
    buildCalendarWeeks,
    buildPlanWeekChangePayload,
    buildMonthSpans,
    extendWeeks,
    extractServerWeekRows,
    formatMonthKey,
    mergeGroupData
} from './calendarFunctions';
import { getValueFromKey } from '../docAutomation/formFunctions';

const LANE_WIDTH = 270;
const EXTEND_WEEK_COUNT = 12;

const parsePickerDateValue = (dateText) => {
    if (!dateText || typeof dateText !== 'string') return '';

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateText)) {
        return dateText;
    }

    const trimmedValue = dateText.trim();
    const matchedParts = trimmedValue.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);

    if (!matchedParts) {
        return '';
    }

    const [, dayPart, monthPart, yearPart] = matchedParts;
    const monthMap = {
        Jan: '01',
        Feb: '02',
        Mar: '03',
        Apr: '04',
        May: '05',
        Jun: '06',
        Jul: '07',
        Aug: '08',
        Sep: '09',
        Oct: '10',
        Nov: '11',
        Dec: '12'
    };

    const monthValue = monthMap[monthPart];
    if (!monthValue) {
        return '';
    }

    return `${yearPart}-${monthValue}-${dayPart.padStart(2, '0')}`;
};

const buildLockedNotification = () => ({
    msgType: 1,
    msg: {
        Code: '',
        Message: 'Plan is locked',
        FormattedMessage: 'Plan is locked',
        Type: 1,
        FormatterParams: null
    },
    messageList: {}
});

const DocCalGridContainer = ({ viewData, deviceInfo }) => {
    const today = React.useMemo(() => new Date(), []);
    const currentMonthKey = React.useMemo(() => formatMonthKey(today), [today]);
    const sarasDnDManager = useSarasCalendarDnDManager(SarasDnDTypes.CARD);
    const controlView = getValueFromKey(viewData, 'childViewResponses.controlView', null);
    const controlViewData = getValueFromKey(controlView, 'viewData', null);
    const searchResultsCriteria = getValueFromKey(viewData, 'dataList.searchResultsCriteria', '');
    const serverWeekRows = React.useMemo(() => extractServerWeekRows(viewData), [viewData]);
    const isTabletWidthOrLess = !!deviceInfo?.isTabletWidthOrLess;

    const [weeks, setWeeks] = React.useState(() => buildCalendarWeeks(today));
    const [groupData, setGroupData] = React.useState(() => mergeGroupData(null, buildCalendarWeeks(today), serverWeekRows));
    const [weekSummaryMap, setWeekSummaryMap] = React.useState({});
    const [monthSummaryMap, setMonthSummaryMap] = React.useState({});
    const [viewNotifications, setViewNotifications] = React.useState(null);
    const calendarControl = useCalendarControl(controlViewData);
    const weekSummaryMapRef = React.useRef({});
    const monthSummaryMapRef = React.useRef({});

    const monthSpans = React.useMemo(() => buildMonthSpans(weeks, currentMonthKey), [weeks, currentMonthKey]);

    // console.log ("calendar search criteria", searchResultsCriteria);

    React.useEffect(() => {
        setGroupData((previousGroupData) => mergeGroupData(previousGroupData, weeks, serverWeekRows));
    }, [weeks, serverWeekRows]);

    React.useEffect(() => {
        setWeekSummaryMap({});
        setMonthSummaryMap({});
    }, [searchResultsCriteria]);

    React.useEffect(() => {
        weekSummaryMapRef.current = weekSummaryMap;
    }, [weekSummaryMap]);

    React.useEffect(() => {
        monthSummaryMapRef.current = monthSummaryMap;
    }, [monthSummaryMap]);

    const extractWeekSummaryList = React.useCallback((response) => {
        const weekSummaryList = getValueFromKey(response, 'data.mainViewDataV5.viewData.weekSummaryList', null)
            || getValueFromKey(response, 'data.viewData.weekSummaryList', null)
            || getValueFromKey(response, 'data.weekSummaryList', null)
            || [];

        return Array.isArray(weekSummaryList) ? weekSummaryList : [];
    }, []);

    const extractMonthSummaryList = React.useCallback((response) => {
        const monthSummaryList = getValueFromKey(response, 'data.mainViewDataV5.viewData.monthSummaryList', null)
            || getValueFromKey(response, 'data.viewData.monthSummaryList', null)
            || getValueFromKey(response, 'data.monthSummaryList', null)
            || [];

        return Array.isArray(monthSummaryList) ? monthSummaryList : [];
    }, []);

    const requestWeekSummary = React.useCallback(async (weekKeys, forceReload = false) => {
        if (!Array.isArray(weekKeys) || weekKeys.length === 0) {
            return;
        }

        const normalizedWeekKeys = [...new Set(
            weekKeys.filter((key) => typeof key === 'string' && key.trim() !== '')
        )];

        if (normalizedWeekKeys.length === 0) {
            return;
        }

        const requestWeekKeys = normalizedWeekKeys.filter((weekKey) => {
            const currentSummary = weekSummaryMapRef.current[weekKey];

            if (currentSummary?.isLoading) {
                return false;
            }

            if (forceReload) {
                return true;
            }

            return !currentSummary || currentSummary.isDirty || !currentSummary.isLoaded;
        });

        if (requestWeekKeys.length === 0) {
            console.log('weeksummary skip-request', {
                forceReload,
                requested: normalizedWeekKeys,
                cacheKeys: Object.keys(weekSummaryMapRef.current || {})
            });
            return;
        }

        console.log('weeksummary request-start', {
            forceReload,
            requested: requestWeekKeys,
            visible: normalizedWeekKeys,
            searchResultsCriteria
        });

        setWeekSummaryMap((previousSummaryMap) => {
            const nextSummaryMap = { ...previousSummaryMap };

            requestWeekKeys.forEach((weekKey) => {
                const currentSummary = nextSummaryMap[weekKey] || {};
                nextSummaryMap[weekKey] = {
                    ...currentSummary,
                    isLoading: true
                };
            });

            return nextSummaryMap;
        });

        const response = await calendarControl.getWeekSummary(searchResultsCriteria, requestWeekKeys);
        const weekSummaryList = extractWeekSummaryList(response);

        console.log('weeksummary response', {
            requested: requestWeekKeys,
            response,
            weekSummaryList
        });

        setWeekSummaryMap((previousSummaryMap) => {
            const nextSummaryMap = { ...previousSummaryMap };

            requestWeekKeys.forEach((weekKey) => {
                const currentSummary = nextSummaryMap[weekKey] || {};
                nextSummaryMap[weekKey] = {
                    ...currentSummary,
                    statistics: Array.isArray(currentSummary.statistics) ? currentSummary.statistics : [],
                    isLoaded: true,
                    isDirty: false,
                    isLoading: false
                };
            });

            weekSummaryList.forEach((weekSummary) => {
                const weekKey = weekSummary?.weekEndDate || '';
                if (!weekKey) return;

                nextSummaryMap[weekKey] = {
                    statistics: Array.isArray(weekSummary.statistics) ? weekSummary.statistics : [],
                    isLoaded: true,
                    isDirty: false,
                    isLoading: false
                };
            });

            // console.log('weeksummary cache-merge', {
            //     requested: requestWeekKeys,
            //     nextSummaryMap
            // });

            return nextSummaryMap;
        });
    }, [calendarControl, extractWeekSummaryList, searchResultsCriteria]);

    const requestMonthSummary = React.useCallback(async (monthKeys, forceReload = false) => {
        if (!Array.isArray(monthKeys) || monthKeys.length === 0) {
            return;
        }

        const normalizedMonthKeys = [...new Set(
            monthKeys.filter((key) => typeof key === 'string' && key.trim() !== '')
        )];

        if (normalizedMonthKeys.length === 0) {
            return;
        }

        const requestMonthKeys = normalizedMonthKeys.filter((monthKey) => {
            const currentSummary = monthSummaryMapRef.current[monthKey];

            if (currentSummary?.isLoading) {
                return false;
            }

            if (forceReload) {
                return true;
            }

            return !currentSummary || currentSummary.isDirty || !currentSummary.isLoaded;
        });

        if (requestMonthKeys.length === 0) {
            return;
        }

        setMonthSummaryMap((previousSummaryMap) => {
            const nextSummaryMap = { ...previousSummaryMap };

            requestMonthKeys.forEach((monthKey) => {
                const currentSummary = nextSummaryMap[monthKey] || {};
                nextSummaryMap[monthKey] = {
                    ...currentSummary,
                    isLoading: true
                };
            });

            return nextSummaryMap;
        });

        const response = await calendarControl.getMonthSummary(searchResultsCriteria, requestMonthKeys);
        const monthSummaryList = extractMonthSummaryList(response);

        setMonthSummaryMap((previousSummaryMap) => {
            const nextSummaryMap = { ...previousSummaryMap };

            requestMonthKeys.forEach((monthKey) => {
                const currentSummary = nextSummaryMap[monthKey] || {};
                nextSummaryMap[monthKey] = {
                    ...currentSummary,
                    statistics: Array.isArray(currentSummary.statistics) ? currentSummary.statistics : [],
                    isLoaded: true,
                    isDirty: false,
                    isLoading: false
                };
            });

            monthSummaryList.forEach((monthSummary) => {
                const monthKey = monthSummary?.monthKey || '';
                if (!monthKey) return;

                nextSummaryMap[monthKey] = {
                    statistics: Array.isArray(monthSummary.statistics) ? monthSummary.statistics : [],
                    isLoaded: true,
                    isDirty: false,
                    isLoading: false
                };
            });

            return nextSummaryMap;
        });
    }, [calendarControl, extractMonthSummaryList, searchResultsCriteria]);

    const markWeekSummariesDirty = React.useCallback((weekKeys) => {
        if (!Array.isArray(weekKeys) || weekKeys.length === 0) {
            return;
        }

        const normalizedWeekKeys = [...new Set(
            weekKeys.filter((key) => typeof key === 'string' && key.trim() !== '')
        )];

        if (normalizedWeekKeys.length === 0) {
            return;
        }

        setWeekSummaryMap((previousSummaryMap) => {
            const nextSummaryMap = { ...previousSummaryMap };

            normalizedWeekKeys.forEach((weekKey) => {
                const currentSummary = nextSummaryMap[weekKey] || {};
                nextSummaryMap[weekKey] = {
                    ...currentSummary,
                    isDirty: true,
                    isLoaded: false
                };
            });

            return nextSummaryMap;
        });
    }, []);

    const markMonthSummariesDirty = React.useCallback((monthKeys) => {
        if (!Array.isArray(monthKeys) || monthKeys.length === 0) {
            return;
        }

        const normalizedMonthKeys = [...new Set(
            monthKeys.filter((key) => typeof key === 'string' && key.trim() !== '')
        )];

        if (normalizedMonthKeys.length === 0) {
            return;
        }

        setMonthSummaryMap((previousSummaryMap) => {
            const nextSummaryMap = { ...previousSummaryMap };

            normalizedMonthKeys.forEach((monthKey) => {
                const currentSummary = nextSummaryMap[monthKey] || {};
                nextSummaryMap[monthKey] = {
                    ...currentSummary,
                    isDirty: true,
                    isLoaded: false
                };
            });

            return nextSummaryMap;
        });
    }, []);

    const findWeekKeyForDocument = React.useCallback((dtid, did) => {
        const groupKeys = groupData?.ViewRowsKeys || [];

        for (let index = 0; index < groupKeys.length; index += 1) {
            const groupKey = groupKeys[index];
            const sourceRow = groupData?.viewRows?.[groupKey]?.rowValues?.matchedData?.childRows?.[`${did}`];

            if (sourceRow && Number(sourceRow?.dtid || 0) === Number(dtid)) {
                return groupKey;
            }
        }

        return '';
    }, [groupData]);

    const onVisibleWeeksChange = React.useCallback((visibleWeekKeys) => {
        console.log('weeksummary visible-weeks', {
            visibleWeekKeys
        });
        requestWeekSummary(visibleWeekKeys);
    }, [requestWeekSummary]);

    const onVisibleMonthsChange = React.useCallback((visibleMonthKeys) => {
        requestMonthSummary(visibleMonthKeys);
    }, [requestMonthSummary]);

    const onDragEnd = async (result) => {
        const dropResult = sarasDnDManager.onDragEnd(groupData, result);
        if (!dropResult) return;

        const changePayload = buildPlanWeekChangePayload(
            dropResult,
            weeks,
            calendarControl.getSetting('snapMode', 'last_date')
        );
        const nextGroupData = applyPlanWeekLocalChanges(dropResult.groupData, dropResult, changePayload);

        // console.log('Plan week change payload:', changePayload);

        setGroupData(nextGroupData);

        if ((changePayload.changeSort?.length || 0) === 0 && (changePayload.changePlanDate?.length || 0) === 0) {
            return;
        }

        const response = await calendarControl.saveWeekChanges(
            changePayload.changeSort,
            changePayload.changePlanDate
        );

        setViewNotifications({
            msgType: response?.data?.isError,
            msg: response?.data?.msg,
            messageList: response?.data?.messageList || {}
        });

        console.log('Plan week change response:', response);

        if (!response?.data?.isError) {
            const affectedWeekKeys = [dropResult.sourceRow.weekKey, dropResult.sourceRow.newWeekKey];
            markWeekSummariesDirty(affectedWeekKeys);
            requestWeekSummary(affectedWeekKeys, true);

            const affectedMonthKeys = affectedWeekKeys
                .filter((weekKey) => typeof weekKey === 'string' && weekKey.length >= 7)
                .map((weekKey) => weekKey.substring(0, 7));
            markMonthSummariesDirty(affectedMonthKeys);
            requestMonthSummary(affectedMonthKeys, true);
        }
    };

    const onExtendLeft = () => {
        setWeeks((previousWeeks) => extendWeeks(previousWeeks, 'left', EXTEND_WEEK_COUNT));
    };

    const onExtendRight = () => {
        setWeeks((previousWeeks) => extendWeeks(previousWeeks, 'right', EXTEND_WEEK_COUNT));
    };

    const onMoveDate = async ({ dtid, did, planDate }) => {
        if (calendarControl?.isCalendarReadOnly) {
            const lockedNotification = buildLockedNotification();
            setViewNotifications(lockedNotification);

            return {
                data: {
                    isError: 1,
                    msg: lockedNotification.msg,
                    messageList: {}
                }
            };
        }

        const nextPlanDate = parsePickerDateValue(planDate);

        if (!dtid || !did || !nextPlanDate) {
            return null;
        }

        const changePlanDate = [{
            dtid,
            did,
            planDate: nextPlanDate
        }];

        const response = await calendarControl.saveWeekChanges([], changePlanDate);
        // console.log('Plan week move date response:', response);

        setViewNotifications({
            msgType: response?.data?.isError,
            msg: response?.data?.msg,
            messageList: response?.data?.messageList || {}
        });

        if (!response?.data?.isError) {
            const sourceWeekKey = findWeekKeyForDocument(dtid, did);
            const targetDate = new Date(`${nextPlanDate}T00:00:00`);
            const targetWeek = Number.isNaN(targetDate.getTime())
                ? null
                : weeks.find((week) => targetDate >= week.weekStartDate && targetDate <= week.weekEndDate);

            setGroupData((previousGroupData) => applyPlanDateMoveLocalChanges(previousGroupData, weeks, changePlanDate[0]));

            const affectedWeekKeys = [sourceWeekKey, targetWeek?.groupKey];
            markWeekSummariesDirty(affectedWeekKeys);
            requestWeekSummary(affectedWeekKeys, true);

            const affectedMonthKeys = affectedWeekKeys
                .filter((weekKey) => typeof weekKey === 'string' && weekKey.length >= 7)
                .map((weekKey) => weekKey.substring(0, 7));
            markMonthSummariesDirty(affectedMonthKeys);
            requestMonthSummary(affectedMonthKeys, true);
        }

        return response;
    };

    return (
        <>
            <SarasMsg notification={viewNotifications} />
            <SarasDnDProvider onDragEnd={onDragEnd}>
                <CalGridBoard
                    weeks={weeks}
                    monthSpans={monthSpans}
                    groupData={groupData}
                    groupKeys={sarasDnDManager.groupKeys(groupData)}
                    sarasDnDManager={sarasDnDManager}
                    laneWidth={LANE_WIDTH}
                    currentMonthKey={currentMonthKey}
                    controlView={controlView}
                    calendarControl={calendarControl}
                    isTabletWidthOrLess={isTabletWidthOrLess}
                    monthSummaryMap={monthSummaryMap}
                    onMoveDate={onMoveDate}
                    weekSummaryMap={weekSummaryMap}
                    onVisibleWeeksChange={onVisibleWeeksChange}
                    onVisibleMonthsChange={onVisibleMonthsChange}
                    onExtendLeft={onExtendLeft}
                    onExtendRight={onExtendRight}
                />
            </SarasDnDProvider>
        </>
    );
};

export default DocCalGridContainer;
